/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, July 2020
 */

import React, { useCallback, useState } from 'react';
import { Popover } from 'react-tiny-popover';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa';
import {
  TouchableOpacity,
  Paragraph,
  styled,
  theme,
  Theme,
} from '@staffino/primitive-controls';
import { LocaleObject } from '../../flow-bridge';
import FlagIcon from './flag-icon';

const ClickableParagraph = styled(Paragraph)({
  cursor: 'pointer',
});

type PopoverWithContainerClassProps = React.ComponentProps<typeof Popover> & {
  className?: string;
};

const PopoverWithContainerClass = ({
  className,
  ...rest
}: PopoverWithContainerClassProps) => (
  <Popover containerClassName={className} {...rest} />
);

const StyledPopover = styled(PopoverWithContainerClass)({
  padding: '0.625rem 0',
  minWidth: '10rem',
  borderRadius: '0.25rem',
  backgroundColor: 'white',
  boxShadow:
    '0 10px 20px 0 rgba(53, 61, 66, 0.1), 0 0 20px 0 rgba(53, 61, 66, 0.2)',
  overflowX: 'hidden',
});

const Arrow = ({ isOpen }) =>
  isOpen ? (
    <FaAngleUp color={theme.colors.text} />
  ) : (
    <FaAngleDown color={theme.colors.lightText} />
  );

const StyledArrow = styled(Arrow)({
  pointerEvents: 'none',
});

const MenuLink = styled(TouchableOpacity)((props: { theme?: Theme }) => ({
  padding: '7px 20px',
  fontSize: '0.875rem',
  color: props.theme.colors.text,
  flexFlow: 'nowrap',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: props.theme.colors.highlight,
    color: props.theme.colors.text,
    textDecoration: 'none',
    '&> .language-icon': {
      boxShadow: '0rem 5px 10px 0rem rgba(163, 178, 191, 1) !important',
    },
  },
}));

type LocaleListProps = {
  onSelect?: (locale: LocaleObject) => void;
  locales: LocaleObject[];
  activeLocale: LocaleObject;
};
const LocaleList = ({
  onSelect,
  locales = [],
  activeLocale,
}: LocaleListProps) => (
  <>
    {locales
      .filter((locale) => locale.code !== activeLocale.code)
      .map((locale) => (
        <MenuLink key={locale.code} onPress={() => onSelect?.(locale)}>
          <FlagIcon
            localeCode={locale.code}
            style={{ marginRight: '0.4rem' }}
          />
          {locale.name}
        </MenuLink>
      ))}
  </>
);

type Props = {
  activeLocale: LocaleObject;
  locales: LocaleObject[];
  onSelect?: (locale: LocaleObject) => void;
};
const LocalePicker = ({ activeLocale, locales, onSelect }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const handleSelect = useCallback(
    (locale) => {
      setIsOpen(false);
      onSelect?.(locale);
    },
    [setIsOpen, onSelect],
  );
  return (
    <StyledPopover
      isOpen={isOpen}
      positions={['bottom', 'right', 'left']}
      align="end"
      content={
        <div>
          <LocaleList
            activeLocale={activeLocale}
            locales={locales}
            onSelect={handleSelect}
          />
        </div>
      }
      onClickOutside={() => setIsOpen(false)}
    >
      <div>
        <ClickableParagraph
          onPress={() => setIsOpen(!isOpen)}
          color={isOpen ? 'text' : 'lightText'}
          fontWeight="bold"
          fontSize="0.875rem"
          mr="1rem"
          flexDirection={'row' as const}
          alignItems="center"
        >
          {activeLocale.shortName?.toUpperCase() ??
            activeLocale.code.toUpperCase()}
          <StyledArrow isOpen={isOpen} />
        </ClickableParagraph>
      </div>
    </StyledPopover>
  );
};

export default LocalePicker;
